import React, { type ErrorInfo, type ReactNode } from 'react';
import PropTypes from 'prop-types';
import { useLocation, type Location } from 'react-router';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { currentUser } from 'selectors/auth.selector';

import Reporter from 'utils/crash-reporting/crashReporter';

const StyledCard = styled(Card)(({ theme }) => {
  return {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    textAlign: 'center',
  };
});

const ReportButton = styled(Button)(({ theme }) => {
  return {
    marginTop: theme.spacing(2),
  };
});

type CurrentUserLike = {
  uid?: string;
  email?: string;
} | null;

type CrashReporterErrorBoundaryProps = {
  currentUser?: CurrentUserLike;
  location?: Location;
  children?: ReactNode;
};

type CrashReporterErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  lastLocation: Location | undefined;
};

// Error boundaries must be class components
class CrashReporterErrorBoundary extends React.Component<
  CrashReporterErrorBoundaryProps,
  CrashReporterErrorBoundaryState
> {
  constructor(props: CrashReporterErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      lastLocation: props.location,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<CrashReporterErrorBoundaryState> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store the error details for reporting
    this.setState({
      errorInfo,
    });

    // Report to our error tracking service
    const { currentUser: currentUserProp } = this.props;
    const { uid, email } = currentUserProp || {};
    Reporter.report(error, errorInfo, {
      userId: uid,
      userEmail: email,
      location: this.props.location?.pathname,
    });
  }

  static getDerivedStateFromProps(
    nextProps: CrashReporterErrorBoundaryProps,
    prevState: CrashReporterErrorBoundaryState
  ): Partial<CrashReporterErrorBoundaryState> | null {
    // Reset error state when location changes
    if (nextProps.location?.pathname !== prevState.lastLocation?.pathname) {
      return {
        hasError: false,
        error: null,
        errorInfo: null,
        lastLocation: nextProps.location,
      };
    }
    return null;
  }

  handleReportFeedback = () => {
    Reporter.showReportErrorDialog({
      error: this.state.error,
      errorInfo: this.state.errorInfo,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <StyledCard>
          <Typography variant="h6" gutterBottom>
            Ooops! That&apos;s embarrassing...
          </Typography>
          <Typography variant="body1" gutterBottom>
            An error has occurred! We have logged it on our systems and we will
            be working on a fix for it soon. If you would like to add more
            information about this issue please press the button below and fill
            up the form.
          </Typography>
          <ReportButton variant="contained" onClick={this.handleReportFeedback}>
            Report feedback
          </ReportButton>
        </StyledCard>
      );
    }
    return this.props.children;
  }
}

CrashReporterErrorBoundary.propTypes = {
  currentUser: PropTypes.object,
  location: PropTypes.object,
  children: PropTypes.node,
};

type ErrorBoundaryWithHooksProps = {
  children?: ReactNode;
};

// Functional component wrapper that uses hooks to get location and user
const ErrorBoundaryWithHooks = ({ children }: ErrorBoundaryWithHooksProps) => {
  const location = useLocation();
  const user = useSelector(currentUser) as CurrentUserLike;

  return (
    <CrashReporterErrorBoundary location={location} currentUser={user}>
      {children}
    </CrashReporterErrorBoundary>
  );
};

ErrorBoundaryWithHooks.propTypes = {
  children: PropTypes.node,
};

export default ErrorBoundaryWithHooks;
